import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Grid,
  Alert,
  Paper,
  TextField,
  Button,
  Snackbar
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WashIcon from '@mui/icons-material/LocalCarWash';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { DashboardService } from '../../../service/dashboard.service';
import { CustomerInfoService, ICustomerInfo } from '../../../service/customer-info.service';

const AccountInfoView: React.FC = () => {
  const [accountData, setAccountData] = useState<{
    id: string;
    displayName: string;
    email?: string;
    subdomain?: string;
  } | null>(null);
  const [customerInfo, setCustomerInfo] = useState<ICustomerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<ICustomerInfo>>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (customerInfo) {
      setFormData({
        name: customerInfo.name,
        company: customerInfo.company,
        position: customerInfo.position || '',
        phone: customerInfo.phone,
        email: customerInfo.email,
        address: customerInfo.address || '',
        washType: customerInfo.washType || '',
        message: customerInfo.message || ''
      });
    }
  }, [customerInfo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch account data
      const accountData = await DashboardService.getDashboard();
      setAccountData({
        id: accountData.id || '',
        displayName: accountData.displayName || 'Nicht verfügbar',
        email: (accountData as any).email,
        subdomain: accountData.subdomain
      });
      
      // Try to fetch customer info
      try {
        const customerData = await CustomerInfoService.getMyCustomerInfo();
        setCustomerInfo(customerData);
      } catch (customerError) {
        console.log('Keine Kundeninformationen gefunden oder nicht verfügbar:', customerError);
        // We continue without customer data - it's not critical
      }
      
      setError(null);
    } catch (err) {
      console.error('Fehler beim Abrufen der Account-Daten:', err);
      setError('Die Account-Informationen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEditToggle = () => {
    if (editMode && customerInfo) {
      // Cancel - reset form
      setFormData({
        name: customerInfo.name,
        company: customerInfo.company,
        position: customerInfo.position || '',
        phone: customerInfo.phone,
        email: customerInfo.email,
        address: customerInfo.address || '',
        washType: customerInfo.washType || '',
        message: customerInfo.message || ''
      });
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Verwende die tatsächliche API-Anfrage
      const updatedCustomerInfo = await CustomerInfoService.updateMyCustomerInfo(formData);
      
      // Aktualisiere den lokalen Zustand mit den neuen Daten
      setCustomerInfo(updatedCustomerInfo);
      
      setEditMode(false);
      setSnackbar({
        open: true,
        message: 'Kundeninformationen erfolgreich aktualisiert',
        severity: 'success'
      });
    } catch (error) {
      console.error('Fehler beim Speichern der Kundeninformationen:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Fehler beim Speichern der Kundeninformationen',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h1">
            Account-Informationen
          </Typography>
        </Box>
        
        {customerInfo && (
          <Button
            variant={editMode ? "outlined" : "contained"}
            color={editMode ? "error" : "primary"}
            startIcon={editMode ? <CancelIcon /> : <EditIcon />}
            onClick={handleEditToggle}
            disabled={saving}
          >
            {editMode ? "Abbrechen" : "Bearbeiten"}
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountCircleIcon sx={{ fontSize: 56, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom>
                {accountData?.displayName}
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Grid container spacing={3}>
            {accountData?.email && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AlternateEmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle2">E-Mail</Typography>
                </Box>
                <Typography variant="body1">{accountData.email}</Typography>
              </Grid>
            )}
            
            {accountData?.subdomain && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle2">Subdomain</Typography>
                </Box>
                <Typography variant="body1">{accountData.subdomain}.washprotech.com</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Customer Information */}
      {customerInfo ? (
        <Card sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Kundeninformationen
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            {editMode ? (
              <form>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                    <TextField
                      fullWidth
                      label="Position"
                      name="position"
                      value={formData.position || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                    <TextField
                      fullWidth
                      label="Telefon"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                    <TextField
                      fullWidth
                      label="E-Mail"
                      name="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Unternehmen"
                      name="company"
                      value={formData.company || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                    <TextField
                      fullWidth
                      label="Adresse"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                    <TextField
                      fullWidth
                      label="Waschstraßen-Typ"
                      name="washType"
                      value={formData.washType || ''}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Zusätzliche Informationen"
                      name="message"
                      value={formData.message || ''}
                      onChange={handleInputChange}
                      multiline
                      rows={4}
                      margin="normal"
                      disabled={saving}
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Speichern...' : 'Speichern'}
                  </Button>
                </Box>
              </form>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="subtitle2">Kontakt</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Name</Typography>
                        <Typography variant="body1">{customerInfo.name}</Typography>
                      </Box>
                      
                      {customerInfo.position && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Position</Typography>
                          <Typography variant="body1">{customerInfo.position}</Typography>
                        </Box>
                      )}
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary">Telefon</Typography>
                        <Typography variant="body1" component="div">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            {customerInfo.phone}
                          </Box>
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">E-Mail</Typography>
                        <Typography variant="body1">{customerInfo.email}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="subtitle2">Unternehmen</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Firma</Typography>
                        <Typography variant="body1">{customerInfo.company}</Typography>
                      </Box>
                      
                      {customerInfo.address && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Adresse</Typography>
                          <Typography variant="body1" component="div">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              {customerInfo.address}
                            </Box>
                          </Typography>
                        </Box>
                      )}
                      
                      {customerInfo.washType && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Waschstraßen-Typ</Typography>
                          <Typography variant="body1" component="div">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <WashIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              {customerInfo.washType}
                            </Box>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                
                {customerInfo.message && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2">Zusätzliche Informationen</Typography>
                      </Box>
                      <Typography variant="body1">{customerInfo.message}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Keine Kundeninformationen verfügbar. Bitte kontaktieren Sie den Support.
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
};

export default AccountInfoView; 